import { Box, Button, Group, RangeSlider, Stack, Text } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getTimezone } from '@/utils/datetime'
import {
  buildIntervalPresets,
  formatIntervalClock,
  intervalFromPreset,
  intervalsMatch,
  matchIntervalPreset,
  pickerDateToZonedIso,
  zonedInstantToPickerDate,
  type IntervalPresetId,
  type TimeInterval,
} from '@/utils/autoScheduleIntervals'

dayjs.extend(utc)
dayjs.extend(timezone)

const HOUR_MS = 60 * 60 * 1000

export function IntervalPicker({
  windowStart,
  windowEnd,
  interval,
  onChange,
  extraChips = [],
}: {
  windowStart: string
  windowEnd: string
  interval: TimeInterval
  onChange: (interval: TimeInterval) => void
  extraChips?: Array<{ id: string; interval: TimeInterval; label: string }>
}) {
  const { t } = useTranslation('scheduling')
  const tz = getTimezone()
  const presets = useMemo(() => buildIntervalPresets(windowStart, windowEnd), [windowStart, windowEnd])
  const selectedExtra = extraChips.find(chip => intervalsMatch(interval, chip.interval))
  const selected = selectedExtra?.id ?? matchIntervalPreset(interval, presets)
  const windowStartMs = dayjs.utc(windowStart).valueOf()
  const windowEndMs = dayjs.utc(windowEnd).valueOf()

  const applyPreset = (presetId: IntervalPresetId) => {
    onChange(intervalFromPreset(presetId, presets, interval))
  }

  const shiftChips: Array<{ id: IntervalPresetId; interval: TimeInterval; label: string }> = []
  if (presets.day) {
    shiftChips.push({
      id: 'day',
      interval: presets.day,
      label: `${t('autoSchedule.dayShift')} ${formatIntervalClock(presets.day, tz)}`,
    })
  }
  if (presets.night) {
    shiftChips.push({
      id: 'night',
      interval: presets.night,
      label: `${t('autoSchedule.nightShift')} ${formatIntervalClock(presets.night, tz)}`,
    })
  }
  shiftChips.push({
    id: 'full',
    interval: presets.full,
    label: `${t('autoSchedule.fullWindow')} ${formatIntervalClock(presets.full, tz)}`,
  })
  shiftChips.push({
    id: 'custom',
    interval,
    label: t('autoSchedule.customWindow'),
  })

  const chipButton = (chip: { id: string; interval: TimeInterval; label: string }, kind: 'preset' | 'extra') => (
    <Button
      key={chip.id}
      size="compact-sm"
      variant={selected === chip.id ? 'filled' : 'light'}
      color={selected === chip.id ? 'blue' : 'gray'}
      onClick={() => (kind === 'extra' ? onChange(chip.interval) : applyPreset(chip.id as IntervalPresetId))}
      styles={{
        root: {
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.01em',
        },
      }}
    >
      {chip.label}
    </Button>
  )

  return (
    <Stack gap="md" style={{ flexShrink: 0 }}>
      <Stack gap="xs" mt="md">
        <Group gap="xs" wrap="wrap">
          {shiftChips.map(chip => chipButton(chip, 'preset'))}
        </Group>
        {extraChips.length > 0 && (
          <>
            <Text size="sm" fw={500}>
              {t('autoSchedule.requirementPresets')}
            </Text>
            <Group gap="xs" wrap="wrap">
              {extraChips.map(chip => chipButton(chip, 'extra'))}
            </Group>
          </>
        )}
      </Stack>
      <Box pb="lg" px="xs">
        <RangeSlider
          min={windowStartMs}
          max={windowEndMs}
          step={HOUR_MS}
          value={[dayjs.utc(interval.start).valueOf(), dayjs.utc(interval.end).valueOf()]}
          onChange={([start, end]) => {
            if (start === undefined || end === undefined) {
              return
            }
            onChange({
              start: dayjs.utc(start).format('YYYY-MM-DDTHH:mm:ss[Z]'),
              end: dayjs.utc(end).format('YYYY-MM-DDTHH:mm:ss[Z]'),
            })
          }}
          label={value => dayjs.utc(value).tz(tz).format('HH:mm')}
          marks={[
            { value: windowStartMs, label: dayjs.utc(windowStart).tz(tz).format('HH:mm') },
            { value: windowEndMs, label: dayjs.utc(windowEnd).tz(tz).format('HH:mm') },
          ]}
        />
      </Box>
      {selected === 'custom' && (
        <Group grow>
          <DateTimePicker
            label={t('autoSchedule.intervalStart')}
            value={zonedInstantToPickerDate(interval.start, tz)}
            onChange={value => {
              if (!value) {
                return
              }
              onChange({
                start: pickerDateToZonedIso(value, tz),
                end: interval.end,
              })
            }}
          />
          <DateTimePicker
            label={t('autoSchedule.intervalEnd')}
            value={zonedInstantToPickerDate(interval.end, tz)}
            onChange={value => {
              if (!value) {
                return
              }
              onChange({
                start: interval.start,
                end: pickerDateToZonedIso(value, tz),
              })
            }}
          />
        </Group>
      )}
      <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatIntervalClock(interval, tz)}
      </Text>
    </Stack>
  )
}
